import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import ProfileService from '../services/profileService';
import AuthService from '../services/authService';
import { getRoleDisplayName, normalizeRole } from '../utils/roleUtils';
import { MEDICAL_DATA_PERMISSION } from '../utils/medicalData';

const useAuthStore = create()(
  devtools(
    persist(
      (set, get) => ({
        // Authentication state
        user: null, // Full user profile from API
        cognitoUser: null, // Cognito user data (from auth context)
        isAuthenticated: false,
        token: null,
        
        // Loading and error states
        isLoading: false,
        error: null,
        lastFetch: null,

        // Actions
        setAuthContext: (authContext) => {
          const cognitoUser = AuthService.getCurrentUser(authContext);
          const isAuthenticated = AuthService.isSessionValid(authContext);
          const token = cognitoUser?.access_token || null;

          set({
            cognitoUser,
            isAuthenticated,
            token,
            error: null
          });

          // If user is authenticated, fetch their profile
          if (isAuthenticated && cognitoUser && token) {
            get().fetchUserProfile();
          } else {
            // Clear user data if not authenticated
            set({ user: null });
          }
        },

        fetchUserProfile: async () => {
          const state = get();
          
          // Avoid duplicate requests
          if (state.isLoading) return;
          
          // Check if we recently fetched (within 10 minutes)
          const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
          if (state.user && state.lastFetch && state.lastFetch > tenMinutesAgo) {
            return state.user;
          }

          if (!state.isAuthenticated || !state.cognitoUser || !state.token) {
            console.warn('Cannot fetch user profile: not authenticated');
            return null;
          }

          set({ isLoading: true, error: null });

          try {
            const userId = state.cognitoUser.profile?.sub;
            if (!userId) {
              throw new Error('User ID not available');
            }

            const userProfile = await ProfileService.getUserProfile(userId, state.token);
            
            set({
              user: userProfile,
              isLoading: false,
              error: null,
              lastFetch: Date.now()
            });
            
            return userProfile;
          } catch (error) {
            console.error('Error fetching user profile:', error);
            
            set({
              error: error.message || 'Failed to load user profile',
              isLoading: false
            });
            
            // Return current user on error
            return state.user;
          }
        },

        updateUserProfile: async (profileData) => {
          const state = get();
          
          if (!state.isAuthenticated || !state.cognitoUser || !state.token) {
            throw new Error('User not authenticated');
          }

          const userId = state.cognitoUser.profile?.sub;
          if (!userId) {
            throw new Error('User ID not available');
          }

          try {
            const updatedProfile = await ProfileService.updateUserProfile(
              userId, 
              profileData, 
              state.token
            );
            
            set((currentState) => ({
              user: { ...currentState.user, ...updatedProfile },
              error: null,
              lastFetch: Date.now() // Update last fetch time
            }));
            
            return updatedProfile;
          } catch (error) {
            console.error('Error updating user profile:', error);
            
            set({
              error: error.message || 'Failed to update user profile'
            });
            
            throw error;
          }
        },

        // Force refresh user profile (ignores cache)
        refreshUserProfile: async () => {
          set({ lastFetch: null });
          return get().fetchUserProfile();
        },

        // Get user display name
        getUserDisplayName: () => {
          const state = get();
          return state.user?.name || 
                 state.cognitoUser?.profile?.name || 
                 state.user?.email || 
                 state.cognitoUser?.profile?.email ||
                 'Usuario';
        },

        getUsername: () => {
            const state = get();
            return state.user?.username;
        },

        // Get user email
        getUserEmail: () => {
          const state = get();
          return state.user?.email || 
                 state.cognitoUser?.profile?.email;
        },

        // Get user ID
        getUserId: () => {
          const state = get();
          return state.user?.id || 
                 state.cognitoUser?.profile?.sub;
        },

        // Get organization ID
        getOrganizationId: () => {
          const state = get();
          return state.user?.organizationId;
        },

        // Get user role
        getUserRole: () => {
          const state = get();
          return getRoleDisplayName(state.user?.role);
        },

        // Get user appRole (for organization management)
        getAppRole: () => {
          const state = get();
          return state.user?.appRole || 'user';
        },

        // Check if the current user has a specific permission.
        // Owner and admin always return true, except for medical data when the organization has it disabled.
        // For 'user' appRole, resolvedPermissions (populated from the user's custom role) is checked.
        hasPermission: (permission) => {
          const state = get();
          if (permission === MEDICAL_DATA_PERMISSION && !get().isMedicalDataEnabled()) return false;
          const appRole = state.user?.appRole;
          if (appRole === 'owner' || appRole === 'admin') return true;
          const perms = state.user?.resolvedPermissions;
          return Array.isArray(perms) && perms.includes(permission);
        },

        // Organization-level switch for clinical features (historia clínica, odontograma,
        // recetas, notas de evolución, fotos). Profiles cached before the backend exposed
        // the flag have no value; the backend still enforces it.
        isMedicalDataEnabled: () => get().user?.medicalDataEnabled !== false,

        // Check if user has specific role
        hasRole: (role) => {
          const state = get();
          const userRole = normalizeRole(state.user?.role);
          const checkRole = normalizeRole(role);
          return userRole === checkRole;
        },

        // Clear all auth data (for logout)
        clearAuth: () => {
          set({
            user: null,
            cognitoUser: null,
            isAuthenticated: false,
            token: null,
            isLoading: false,
            error: null,
            lastFetch: null
          });
        },

        // Clear error
        clearError: () => {
          set({ error: null });
        },

        // Check if user profile is complete
        isProfileComplete: () => {
          const state = get();
          const user = state.user;
          
          if (!user) return false;
          
          // Check for required fields
          return !!(
            user.name &&
            user.email &&
            user.organizationId
          );
        },

        // Get missing profile fields
        getMissingProfileFields: () => {
          const state = get();
          const user = state.user;
          const missing = [];
          
          if (!user) return ['profile'];
          
          if (!user.name) missing.push('name');
          if (!user.email) missing.push('email');
          if (!user.organizationId) missing.push('organizationId');
          
          return missing;
        }
      }),
      {
        name: 'dental-auth',
        partialize: (state) => ({
          // Only persist user data, not temporary states
          user: state.user,
          lastFetch: state.lastFetch
        })
      }
    ),
    {
      name: 'auth-store'
    }
  )
);

export default useAuthStore;
